import React from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Play, Plus, Trash2, X, FlaskConical, AlertTriangle, CheckCircle, Loader2,
  Info, Download, Settings, Save, Users, User, Eye, Search, Briefcase,
} from 'lucide-react';
import { getSeverityColor } from '../../utils/styleUtils';

const SEVERITIES = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'];
const OPERATORS = ['>', '<', '>=', '<=', '==', '!=', 'IN', 'BETWEEN'];
const MONITOR_TYPES = [
  { value: 'TRANSACCION_UNICA', label: 'Transacción Única', desc: 'Detecta transacciones individuales' },
  { value: 'ACUMULACION_MONTO', label: 'Acumulación de Monto', desc: 'Suma de montos en período' },
  { value: 'ACUMULACION_CANTIDAD', label: 'Acumulación de Cantidad', desc: 'Número de operaciones en período' },
  { value: 'FRACCIONAMIENTO', label: 'Fraccionamiento', desc: 'Montos similares repetidos' }
];
const VENTANAS = [
  { value: 'DIARIO', label: 'Diario', days: 1 },
  { value: 'SEMANAL', label: 'Semanal', days: 7 },
  { value: 'MENSUAL', label: 'Mensual', days: 30 }
];
const RISK_ZONE_TYPES = [
  { value: 'cbu_prefix', label: 'Prefijo de CBU' },
  { value: 'banco', label: 'Nombre de Banco' },
  { value: 'cuit_prefix', label: 'Prefijo de CUIT' },
  { value: 'exact', label: 'Valor Exacto' }
];

const MonitorForm = ({ formData, setFormData, addCriterio, removeCriterio, updateCriterio }) => {
  const isAcumulacion = formData.tipo_monitor && formData.tipo_monitor !== 'TRANSACCION_UNICA';

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="nombre">Nombre del Monitor *</Label>
        <Input
          id="nombre"
          data-testid="monitor-name-input"
          value={formData.nombre}
          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
          placeholder="Ej: Detección de montos altos"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="descripcion">Descripción *</Label>
        <Textarea
          id="descripcion"
          data-testid="monitor-description-input"
          value={formData.descripcion}
          onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
          placeholder="Describe qué detecta este monitor..."
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label>Tipo de Monitor *</Label>
        <Select
          value={formData.tipo_monitor || 'TRANSACCION_UNICA'}
          onValueChange={(value) => setFormData({
            ...formData,
            tipo_monitor: value,
            ventana_temporal: value !== 'TRANSACCION_UNICA' ? 'DIARIO' : null
          })}
        >
          <SelectTrigger data-testid="monitor-type-select">
            <SelectValue placeholder="Seleccionar tipo" />
          </SelectTrigger>
          <SelectContent>
            {MONITOR_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                <div className="flex flex-col">
                  <span>{type.label}</span>
                  <span className="text-xs text-slate-500">{type.desc}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isAcumulacion && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
          <h4 className="text-sm font-medium text-blue-900">Configuración de Acumulación</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-blue-800">Ventana Temporal *</Label>
              <Select
                value={formData.ventana_temporal || 'DIARIO'}
                onValueChange={(value) => setFormData({ ...formData, ventana_temporal: value })}
              >
                <SelectTrigger><SelectValue placeholder="Seleccionar período" /></SelectTrigger>
                <SelectContent>
                  {VENTANAS.map((v) => (
                    <SelectItem key={v.value} value={v.value}>{v.label} ({v.days} días)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-blue-800">
                {formData.tipo_monitor === 'ACUMULACION_MONTO' ? 'Umbral de Monto ($)' :
                 formData.tipo_monitor === 'ACUMULACION_CANTIDAD' ? 'Umbral de Operaciones' :
                 'Mínimo de Repeticiones'}
              </Label>
              <Input
                type="number"
                value={formData.umbral_acumulacion || ''}
                onChange={(e) => setFormData({ ...formData, umbral_acumulacion: parseFloat(e.target.value) || null })}
                placeholder={
                  formData.tipo_monitor === 'ACUMULACION_MONTO' ? '500000' :
                  formData.tipo_monitor === 'ACUMULACION_CANTIDAD' ? '10' : '3'
                }
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-blue-800">Campo de Agrupación</Label>
            <Select
              value={formData.campo_agrupacion || 'cvu_mov'}
              onValueChange={(value) => setFormData({ ...formData, campo_agrupacion: value })}
            >
              <SelectTrigger><SelectValue placeholder="Seleccionar campo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cvu_mov">CVU/CBU (cuenta WAYA)</SelectItem>
                <SelectItem value="cbu_cvu_origen">CBU/CVU Origen</SelectItem>
                <SelectItem value="cbu_cvu_destino">CBU/CVU Destino</SelectItem>
                <SelectItem value="cuit_cuil_origen">CUIT/CUIL Origen</SelectItem>
                <SelectItem value="cuit_cuil_destino">CUIT/CUIL Destino</SelectItem>
                <SelectItem value="cuit">CUIT (titular)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Severidad *</Label>
          <Select
            value={formData.severidad}
            onValueChange={(value) => setFormData({ ...formData, severidad: value })}
          >
            <SelectTrigger data-testid="monitor-severity-select">
              <SelectValue placeholder="Seleccionar severidad" />
            </SelectTrigger>
            <SelectContent>
              {SEVERITIES.map((sev) => (
                <SelectItem key={sev} value={sev}>{sev}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Estado</Label>
          <div className="flex items-center space-x-2 pt-2">
            <Switch
              checked={formData.activo}
              onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
            />
            <span className="text-sm text-slate-600">
              {formData.activo ? 'Activo' : 'Inactivo'}
            </span>
          </div>
        </div>
      </div>

      {!isAcumulacion && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Criterios de Detección</Label>
            <Button type="button" variant="outline" size="sm" onClick={addCriterio}>
              <Plus size={14} className="mr-1" /> Agregar
            </Button>
          </div>
          {formData.criterios.map((criterio, idx) => (
            <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">Criterio {idx + 1}</span>
                {formData.criterios.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeCriterio(idx)}
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700">
                    <X size={14} />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Campo (ej: monto)" value={criterio.campo}
                  onChange={(e) => updateCriterio(idx, 'campo', e.target.value)} className="text-sm" />
                <Select value={criterio.operador} onValueChange={(value) => updateCriterio(idx, 'operador', value)}>
                  <SelectTrigger className="text-sm"><SelectValue placeholder="Operador" /></SelectTrigger>
                  <SelectContent>
                    {OPERATORS.map((op) => (<SelectItem key={op} value={op}>{op}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Input placeholder="Valor" value={criterio.valor}
                  onChange={(e) => updateCriterio(idx, 'valor', e.target.value)} className="text-sm" />
              </div>
              <Input placeholder="Agrupar por (opcional, ej: cuit)" value={criterio.agrupacion || ''}
                onChange={(e) => updateCriterio(idx, 'agrupacion', e.target.value)} className="text-sm" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MonitorsDialogs = ({
  // Form state
  formData, setFormData, addCriterio, removeCriterio, updateCriterio,
  // Create dialog
  isCreateDialogOpen, setIsCreateDialogOpen, handleCreateMonitor,
  // Edit dialog
  isEditDialogOpen, setIsEditDialogOpen, handleUpdateMonitor,
  // Delete dialog
  isDeleteDialogOpen, setIsDeleteDialogOpen, handleDeleteMonitor,
  selectedMonitor, saving,
  // Backtest config
  isBacktestConfigOpen, setIsBacktestConfigOpen,
  backtestConfig, setBacktestConfig, executeBacktest,
  // Backtest results
  isBacktestDialogOpen, setIsBacktestDialogOpen,
  backtestLoading, backtestResults,
  selectedBacktestResults, setSelectedBacktestResults,
  backtestFilters, setBacktestFilters,
  showBacktestFilters, setShowBacktestFilters,
  getFilteredBacktestResults, clearBacktestFilters,
  toggleBacktestSelection, handleCreateHistoricalAlert,
  creatingAlert, creatingCaseFromBacktest, handleCreateCaseFromBacktest,
  // Group dialog
  isGroupDialogOpen, setIsGroupDialogOpen,
  selectedGroup, groupFormData, setGroupFormData,
  monitors, toggleMonitorInGroup, handleSaveGroup,
  // Group execution
  isGroupExecutionOpen, setIsGroupExecutionOpen,
  groupExecutionLoading, groupExecutionResults,
  // Install predefined
  isInstallDialogOpen, setIsInstallDialogOpen,
  handleInstallPredefined, installing,
  // Risk zone
  isRiskZoneDialogOpen, setIsRiskZoneDialogOpen,
  riskZoneFormData, setRiskZoneFormData, handleSaveRiskZone,
  // Execute all
  isExecuteAllDialogOpen, setIsExecuteAllDialogOpen,
  executeAllLoading, executeAllResults, setExecuteAllResults,
  generateAlertsAfterBacktest, handleGenerateAlertsFromResults,
  alertGenerationMode, setAlertGenerationMode,
  selectedMonitorsForAlerts, setSelectedMonitorsForAlerts,
  forzarNuevosCasos, setForzarNuevosCasos,
  modoEjecucion, setModoEjecucion,
  clienteSearch, setClienteSearch,
  clienteFilter, setClienteFilter,
  clientesEncontrados, setClientesEncontrados,
  buscandoCliente,
  clienteSeleccionado, setClienteSeleccionado,
  // Global config
  showGlobalConfigDialog, setShowGlobalConfigDialog,
  globalConfig, setGlobalConfig, saveGlobalConfig,
}) => (
  <>
    {/* Create Monitor Dialog */}
    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Crear Nuevo Monitor</DialogTitle>
          <DialogDescription className="text-slate-600">
            Define las reglas de detección para identificar patrones sospechosos.
          </DialogDescription>
        </DialogHeader>
        <MonitorForm formData={formData} setFormData={setFormData}
          addCriterio={addCriterio} removeCriterio={removeCriterio} updateCriterio={updateCriterio} />
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancelar</Button>
          <Button data-testid="save-monitor-button" onClick={handleCreateMonitor}
            disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? 'Guardando...' : 'Crear Monitor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Edit Monitor Dialog */}
    <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Editar Monitor</DialogTitle>
          <DialogDescription className="text-slate-600">
            Modifica las reglas de detección del monitor.
          </DialogDescription>
        </DialogHeader>
        <MonitorForm formData={formData} setFormData={setFormData}
          addCriterio={addCriterio} removeCriterio={removeCriterio} updateCriterio={updateCriterio} />
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancelar</Button>
          <Button data-testid="update-monitor-button" onClick={handleUpdateMonitor}
            disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Delete Monitor Dialog */}
    <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Eliminar Monitor</DialogTitle>
          <DialogDescription className="text-slate-600">
            ¿Estás seguro de que deseas eliminar el monitor "{selectedMonitor?.nombre}"?
            Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancelar</Button>
          <Button data-testid="confirm-delete-monitor-button" onClick={handleDeleteMonitor}
            disabled={saving} className="bg-red-600 hover:bg-red-700 text-white">
            {saving ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Backtest Config Dialog */}
    <Dialog open={isBacktestConfigOpen} onOpenChange={setIsBacktestConfigOpen}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <FlaskConical className="text-purple-600" size={20} />
            Configurar Backtesting
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {selectedMonitor?.nombre}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label className="text-slate-700">CUIT del Cliente (opcional)</Label>
            <Input placeholder="Ej: 20123456789" value={backtestConfig.cuit}
              onChange={(e) => setBacktestConfig(prev => ({ ...prev, cuit: e.target.value }))} className="mt-1" />
          </div>
          <div>
            <Label className="text-slate-700">Fecha Desde</Label>
            <Input type="date" value={backtestConfig.fecha_desde}
              onChange={(e) => setBacktestConfig(prev => ({ ...prev, fecha_desde: e.target.value }))} className="mt-1" />
          </div>
          <div>
            <Label className="text-slate-700">Fecha Hasta</Label>
            <Input type="date" value={backtestConfig.fecha_hasta}
              onChange={(e) => setBacktestConfig(prev => ({ ...prev, fecha_hasta: e.target.value }))} className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsBacktestConfigOpen(false)}>Cancelar</Button>
          <Button onClick={executeBacktest} className="bg-purple-600 hover:bg-purple-700 text-white">
            <FlaskConical size={16} className="mr-2" />Ejecutar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Backtest Results Dialog */}
    <Dialog open={isBacktestDialogOpen} onOpenChange={setIsBacktestDialogOpen}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <FlaskConical className="text-purple-600" size={20} />
            Resultados del Backtesting
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto">
          {backtestLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="animate-spin text-purple-600 mb-4" size={40} />
              <p className="text-slate-600">Ejecutando backtesting...</p>
            </div>
          ) : backtestResults?.error ? (
            <div className="flex flex-col items-center justify-center py-12 text-red-600">
              <AlertTriangle size={40} className="mb-4" />
              <p className="font-medium">Error en el backtesting</p>
              <p className="text-sm text-slate-500 mt-1">{backtestResults.message}</p>
            </div>
          ) : backtestResults ? (
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-purple-700">{backtestResults.total_coincidencias}</p>
                    <p className="text-sm text-purple-600">Coincidencias</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      {backtestResults.rango_datos_desde ? new Date(backtestResults.rango_datos_desde).toLocaleDateString() : 'N/A'}
                    </p>
                    <p className="text-sm text-slate-500">Desde</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      {backtestResults.rango_datos_hasta ? new Date(backtestResults.rango_datos_hasta).toLocaleDateString() : 'N/A'}
                    </p>
                    <p className="text-sm text-slate-500">Hasta</p>
                  </div>
                </div>
              </div>

              {backtestResults.coincidencias && backtestResults.coincidencias.length > 0 ? (
                <div className="space-y-3">
                  {/* Panel de Filtros */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg">
                    <button className="w-full px-4 py-3 flex items-center justify-between text-slate-700 hover:bg-slate-100"
                      onClick={() => setShowBacktestFilters(!showBacktestFilters)}>
                      <span className="flex items-center gap-2 font-medium">
                        <Settings size={16} />Filtros de Resultados
                        {(backtestFilters.montoMinimo || backtestFilters.montoMaximo || backtestFilters.minTransacciones) && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">Activos</Badge>
                        )}
                      </span>
                      <span className="text-sm text-slate-500">
                        {getFilteredBacktestResults().length} de {backtestResults.coincidencias.length} resultados
                      </span>
                    </button>
                    {showBacktestFilters && (
                      <div className="px-4 pb-4 border-t border-slate-200">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
                          <div>
                            <Label className="text-xs text-slate-600">Monto Mínimo</Label>
                            <Input type="number" placeholder="$0" value={backtestFilters.montoMinimo}
                              onChange={(e) => setBacktestFilters(f => ({ ...f, montoMinimo: e.target.value }))} className="h-8 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Monto Máximo</Label>
                            <Input type="number" placeholder="Sin límite" value={backtestFilters.montoMaximo}
                              onChange={(e) => setBacktestFilters(f => ({ ...f, montoMaximo: e.target.value }))} className="h-8 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Mín. Transacciones</Label>
                            <Input type="number" placeholder="1" value={backtestFilters.minTransacciones}
                              onChange={(e) => setBacktestFilters(f => ({ ...f, minTransacciones: e.target.value }))} className="h-8 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Fecha Desde</Label>
                            <Input type="date" value={backtestFilters.fechaDesde}
                              onChange={(e) => setBacktestFilters(f => ({ ...f, fechaDesde: e.target.value }))} className="h-8 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Fecha Hasta</Label>
                            <Input type="date" value={backtestFilters.fechaHasta}
                              onChange={(e) => setBacktestFilters(f => ({ ...f, fechaHasta: e.target.value }))} className="h-8 text-sm" />
                          </div>
                        </div>
                        <div className="flex justify-end mt-3">
                          <Button variant="ghost" size="sm" onClick={clearBacktestFilters} className="text-slate-600">
                            Limpiar Filtros
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Barra de selección múltiple */}
                  {selectedBacktestResults.length > 0 && (
                    <div className="bg-purple-600 text-white px-4 py-3 rounded-lg flex items-center justify-between">
                      <span className="font-medium">{selectedBacktestResults.length} resultado(s) seleccionado(s)</span>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setSelectedBacktestResults([])}
                          className="bg-white/20 hover:bg-white/30 text-white border-0">Limpiar</Button>
                        <Button size="sm" onClick={handleCreateCaseFromBacktest} disabled={creatingCaseFromBacktest}
                          className="bg-white text-purple-600 hover:bg-purple-50">
                          {creatingCaseFromBacktest ? (
                            <Loader2 size={14} className="animate-spin mr-2" />
                          ) : (
                            <Briefcase size={14} className="mr-2" />
                          )}
                          Crear Caso
                        </Button>
                      </div>
                    </div>
                  )}

                  {getFilteredBacktestResults().length > 0 ? (
                    <div className="border border-slate-200 rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="w-10">
                              <input type="checkbox"
                                checked={selectedBacktestResults.length === getFilteredBacktestResults().length && getFilteredBacktestResults().length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedBacktestResults(
                                      getFilteredBacktestResults().map((m, i) => ({ key: `${m.cuenta_id}-${i}`, match: m, idx: i }))
                                    );
                                  } else {
                                    setSelectedBacktestResults([]);
                                  }
                                }}
                                className="rounded" />
                            </TableHead>
                            <TableHead>Cuenta/CUIT</TableHead>
                            <TableHead>Detalle</TableHead>
                            <TableHead>Monto</TableHead>
                            <TableHead>Txns</TableHead>
                            <TableHead className="text-right">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getFilteredBacktestResults().map((match, idx) => {
                            const isSelected = selectedBacktestResults.some(s => s.key === `${match.cuenta_id}-${idx}`);
                            return (
                              <TableRow key={idx} className={isSelected ? 'bg-purple-50' : ''}>
                                <TableCell>
                                  <input type="checkbox" checked={isSelected}
                                    onChange={() => toggleBacktestSelection(match, idx)} className="rounded" />
                                </TableCell>
                                <TableCell className="font-mono text-sm">{match.cuenta_id}</TableCell>
                                <TableCell className="text-sm max-w-[150px] truncate">{match.titulo_detalle}</TableCell>
                                <TableCell className="text-sm font-medium text-green-700">
                                  {match.monto_total ? `$${match.monto_total.toLocaleString()}` : '-'}
                                </TableCell>
                                <TableCell className="text-sm text-center">
                                  {match.cantidad_transacciones || match.movimientos_ids?.length || '-'}
                                </TableCell>
                                <TableCell className="text-right">
                                  {match.alertaCreada ? (
                                    <span className="text-green-600 text-sm flex items-center justify-end">
                                      <CheckCircle size={16} className="mr-1" />Creada
                                    </span>
                                  ) : (
                                    <Button size="sm" onClick={() => handleCreateHistoricalAlert(match)}
                                      disabled={creatingAlert[match.cuenta_id]}
                                      className="bg-purple-600 hover:bg-purple-700 text-white">
                                      {creatingAlert[match.cuenta_id] ? (
                                        <Loader2 size={12} className="animate-spin" />
                                      ) : 'Generar Alerta'}
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                      <p>No hay resultados que coincidan con los filtros aplicados</p>
                      <Button variant="ghost" size="sm" onClick={clearBacktestFilters} className="mt-2">Limpiar filtros</Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">No se encontraron coincidencias</div>
              )}
            </div>
          ) : null}
        </div>
        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => setIsBacktestDialogOpen(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Group Dialog */}
    <Dialog open={isGroupDialogOpen} onOpenChange={setIsGroupDialogOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900">
            {selectedGroup ? 'Editar Grupo' : 'Nuevo Grupo de Monitores'}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Agrupa monitores para ejecutarlos juntos
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Nombre del Grupo *</Label>
            <Input value={groupFormData.nombre}
              onChange={(e) => setGroupFormData({ ...groupFormData, nombre: e.target.value })}
              placeholder="Ej: Control Diario" />
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea value={groupFormData.descripcion}
              onChange={(e) => setGroupFormData({ ...groupFormData, descripcion: e.target.value })}
              placeholder="Describe el propósito del grupo..." rows={2} />
          </div>
          <div>
            <Label className="mb-2 block">Monitores del Grupo</Label>
            <div className="border border-slate-200 rounded-lg max-h-60 overflow-y-auto">
              {monitors.map((monitor) => (
                <div key={monitor.id}
                  className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <Checkbox checked={groupFormData.monitor_ids?.includes(monitor.id)}
                      onCheckedChange={() => toggleMonitorInGroup(monitor.id)} />
                    <div>
                      <p className="text-sm font-medium">{monitor.nombre}</p>
                      <p className="text-xs text-slate-500">{monitor.descripcion?.slice(0, 50)}...</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(monitor.severidad)}`}>
                    {monitor.severidad}
                  </span>
                </div>
              ))}
              {monitors.length === 0 && (
                <p className="text-center py-4 text-slate-500">No hay monitores disponibles</p>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {groupFormData.monitor_ids?.length || 0} monitores seleccionados
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsGroupDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveGroup} disabled={saving}
            className="bg-green-600 hover:bg-green-700 text-white">
            {saving ? 'Guardando...' : selectedGroup ? 'Guardar Cambios' : 'Crear Grupo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Group Execution Results Dialog */}
    <Dialog open={isGroupExecutionOpen} onOpenChange={setIsGroupExecutionOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Play className="text-green-600" size={20} />
            Resultado de Ejecución del Grupo
          </DialogTitle>
          <DialogDescription>{selectedGroup?.nombre}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto">
          {groupExecutionLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="animate-spin text-green-600 mb-4" size={40} />
              <p className="text-slate-600">Ejecutando monitores del grupo...</p>
            </div>
          ) : groupExecutionResults?.error ? (
            <div className="flex flex-col items-center justify-center py-12 text-red-600">
              <AlertTriangle size={40} className="mb-4" />
              <p>{groupExecutionResults.message}</p>
            </div>
          ) : groupExecutionResults ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-700">{groupExecutionResults.monitores_ejecutados}</p>
                    <p className="text-sm text-green-600">Monitores Ejecutados</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-700">{groupExecutionResults.total_coincidencias}</p>
                    <p className="text-sm text-blue-600">Coincidencias</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-700">{groupExecutionResults.total_alertas_generadas}</p>
                    <p className="text-sm text-purple-600">Alertas Generadas</p>
                  </div>
                </div>
              </div>
              <div className="border border-slate-200 rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Monitor</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Coincidencias</TableHead>
                      <TableHead className="text-right">Alertas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupExecutionResults.resultados_por_monitor?.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{r.monitor_nombre}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs ${
                            r.estado === 'EJECUTADO' ? 'bg-green-100 text-green-700' :
                            r.estado === 'OMITIDO' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>{r.estado}</span>
                        </TableCell>
                        <TableCell className="text-right">{r.coincidencias || 0}</TableCell>
                        <TableCell className="text-right">{r.alertas_generadas || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter className="border-t pt-4">
          <Button className="bg-slate-700 hover:bg-slate-800 text-white" onClick={() => setIsGroupExecutionOpen(false)}>
            <CheckCircle size={16} className="mr-2" />Cerrar Resumen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Install Predefined Dialog */}
    <Dialog open={isInstallDialogOpen} onOpenChange={setIsInstallDialogOpen}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Download className="text-purple-600" size={20} />
            Instalar Configuración Predefinida
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Esto instalará 19 monitores de detección de fraude y 6 grupos organizados por categoría.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-3">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-medium text-purple-900 mb-2">Monitores incluidos:</h4>
            <ul className="text-sm text-purple-700 space-y-1">
              <li>&#8226; Patrones de montos similares (Fraccionamiento)</li>
              <li>&#8226; Horarios inusuales (23:00-06:00)</li>
              <li>&#8226; Múltiples orígenes a mismo destino</li>
              <li>&#8226; Monto acumulado diario elevado</li>
              <li>&#8226; Más de 20 movimientos por día</li>
              <li>&#8226; Y 14 monitores más...</li>
            </ul>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-2">Grupos incluidos:</h4>
            <ul className="text-sm text-green-700 space-y-1">
              <li>&#8226; Control Diario Básico</li>
              <li>&#8226; Detección de Smurfing</li>
              <li>&#8226; Cuentas de Paso</li>
              <li>&#8226; Comportamiento Anómalo</li>
              <li>&#8226; Control Nocturno</li>
              <li>&#8226; Alta Prioridad - Críticos</li>
            </ul>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <strong>Nota:</strong> Los monitores que ya existan no serán duplicados.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsInstallDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleInstallPredefined} disabled={installing}
            className="bg-purple-600 hover:bg-purple-700 text-white">
            {installing ? (
              <><Loader2 className="animate-spin mr-2" size={16} />Instalando...</>
            ) : (
              <><Download size={16} className="mr-2" />Instalar Todo</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Risk Zone Dialog */}
    <Dialog open={isRiskZoneDialogOpen} onOpenChange={setIsRiskZoneDialogOpen}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-slate-900">
            {riskZoneFormData.id ? 'Editar Zona de Riesgo' : 'Nueva Zona de Riesgo'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Nombre *</Label>
            <Input value={riskZoneFormData.nombre}
              onChange={(e) => setRiskZoneFormData({ ...riskZoneFormData, nombre: e.target.value })}
              placeholder="Ej: Banco XYZ" />
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={riskZoneFormData.tipo}
              onValueChange={(value) => setRiskZoneFormData({ ...riskZoneFormData, tipo: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RISK_ZONE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Valor *</Label>
            <Input value={riskZoneFormData.valor}
              onChange={(e) => setRiskZoneFormData({ ...riskZoneFormData, valor: e.target.value })}
              placeholder={
                riskZoneFormData.tipo === 'cbu_prefix' ? 'Ej: 0070' :
                riskZoneFormData.tipo === 'banco' ? 'Ej: Banco XYZ' : 'Valor a detectar'
              } />
          </div>
          <div>
            <Label>Nivel de Riesgo</Label>
            <Select value={riskZoneFormData.nivel_riesgo}
              onValueChange={(value) => setRiskZoneFormData({ ...riskZoneFormData, nivel_riesgo: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALTO">Alto</SelectItem>
                <SelectItem value="MEDIO">Medio</SelectItem>
                <SelectItem value="BAJO">Bajo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Descripción (opcional)</Label>
            <Textarea value={riskZoneFormData.descripcion}
              onChange={(e) => setRiskZoneFormData({ ...riskZoneFormData, descripcion: e.target.value })}
              placeholder="Notas adicionales..." rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsRiskZoneDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveRiskZone} disabled={saving}
            className="bg-red-600 hover:bg-red-700 text-white">
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Execute All Monitors Dialog */}
    <Dialog
      open={isExecuteAllDialogOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (executeAllLoading) return;
          if (executeAllResults && executeAllResults.total_coincidencias > 0) {
            const confirmed = window.confirm('¿Está seguro que desea cerrar? Los resultados del análisis se perderán y deberá volver a ejecutar los monitores para generar alertas.');
            if (!confirmed) return;
          }
        }
        setIsExecuteAllDialogOpen(open);
      }}
    >
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-white"
        onInteractOutside={(e) => {
          if (executeAllLoading || (executeAllResults && executeAllResults.total_coincidencias > 0)) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (executeAllLoading || (executeAllResults && executeAllResults.total_coincidencias > 0)) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Play className="text-green-600" size={20} />
            Ejecutar Todos los Monitores
          </DialogTitle>
          <DialogDescription>
            Resumen de coincidencias encontradas - Revise antes de generar alertas
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {executeAllLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="animate-spin text-green-600 mb-4" size={40} />
              <p className="text-slate-600">Analizando {monitors.filter(m => m.activo).length} monitores activos...</p>
              <p className="text-xs text-slate-400 mt-2">Esto puede tomar unos segundos</p>
            </div>
          ) : executeAllResults ? (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-white text-center">
                  <p className="text-3xl font-bold text-slate-900">{executeAllResults.total_monitores}</p>
                  <p className="text-sm text-slate-500">Monitores Analizados</p>
                </Card>
                <Card className="p-4 bg-green-50 border-green-200 text-center">
                  <p className="text-3xl font-bold text-green-700">{executeAllResults.con_coincidencias}</p>
                  <p className="text-sm text-green-600">Con Coincidencias</p>
                </Card>
                <Card className="p-4 bg-blue-50 border-blue-200 text-center">
                  <p className="text-3xl font-bold text-blue-700">{executeAllResults.total_coincidencias}</p>
                  <p className="text-sm text-blue-600">Total Coincidencias</p>
                </Card>
                <Card className="p-4 bg-orange-50 border-orange-200 text-center">
                  <p className="text-3xl font-bold text-orange-700">
                    {executeAllResults.detalle_por_monitor.filter(m => m.severidad === 'CRITICA' && m.coincidencias > 0).length}
                  </p>
                  <p className="text-sm text-orange-600">Críticos con Alertas</p>
                </Card>
              </div>

              {/* Results Table */}
              <Card className="bg-white border-slate-200">
                <div className="p-3 bg-slate-50 border-b">
                  <h3 className="font-medium text-slate-900">Detalle por Monitor</h3>
                  <p className="text-xs text-slate-500">Ordenados por cantidad de coincidencias</p>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Monitor</TableHead>
                        <TableHead>Severidad</TableHead>
                        <TableHead className="text-right">Coincidencias</TableHead>
                        <TableHead>Preview</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {executeAllResults.detalle_por_monitor.map((monitor, idx) => (
                        <TableRow key={idx} className={monitor.coincidencias > 0 ? 'bg-green-50' : ''}>
                          <TableCell>
                            <div><p className="font-medium text-sm">{monitor.nombre}</p></div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getSeverityColor(monitor.severidad)}>{monitor.severidad}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`font-bold ${monitor.coincidencias > 0 ? 'text-green-700' : 'text-slate-400'}`}>
                              {monitor.coincidencias}
                            </span>
                          </TableCell>
                          <TableCell>
                            {monitor.preview?.length > 0 ? (
                              <span className="text-xs text-slate-500">
                                {monitor.preview[0]?.cuenta_id?.substring(0, 15)}... +{monitor.coincidencias - 1} más
                              </span>
                            ) : monitor.error ? (
                              <span className="text-xs text-red-500">Error</span>
                            ) : (
                              <span className="text-xs text-slate-400">Sin coincidencias</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>

              {/* Warning if many alerts */}
              {executeAllResults.total_coincidencias > 100 && (
                <Card className="p-4 bg-amber-50 border-amber-200">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertTriangle size={18} />
                    <p className="text-sm">
                      <strong>Atención:</strong> Se encontraron {executeAllResults.total_coincidencias} coincidencias.
                      Considere usar el modo "Casos por Tipo" para crear un caso por cada monitor.
                    </p>
                  </div>
                </Card>
              )}

              {/* Modo de Ejecución */}
              {executeAllResults.total_coincidencias > 0 && (
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Settings size={16} />Modo de Ejecución
                  </h4>
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant={modoEjecucion === 'TODOS' ? 'default' : 'outline'}
                        onClick={() => { setModoEjecucion('TODOS'); setClienteFilter(''); setClienteSeleccionado(null); }}
                        className={modoEjecucion === 'TODOS' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}>
                        <Users size={14} className="mr-1" />Todos los Clientes
                      </Button>
                      <Button size="sm" variant={modoEjecucion === 'CLIENTE' ? 'default' : 'outline'}
                        onClick={() => setModoEjecucion('CLIENTE')}
                        className={modoEjecucion === 'CLIENTE' ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}>
                        <User size={14} className="mr-1" />Cliente Específico
                      </Button>
                      <Button size="sm" variant={modoEjecucion === 'VISUALIZACION' ? 'default' : 'outline'}
                        onClick={() => setModoEjecucion('VISUALIZACION')}
                        className={modoEjecucion === 'VISUALIZACION' ? 'bg-purple-600 hover:bg-purple-700 text-white' : ''}>
                        <Eye size={14} className="mr-1" />Solo Visualizar
                      </Button>
                    </div>
                    <div className="text-xs text-slate-600 bg-white p-2 rounded border">
                      {modoEjecucion === 'TODOS' && (
                        <p>Ejecutar monitores para <strong>todos los clientes</strong> y crear/actualizar casos automáticamente.</p>
                      )}
                      {modoEjecucion === 'CLIENTE' && (
                        <p>Ejecutar monitores solo para un <strong>cliente específico</strong> (buscar por CUIT, CVU o nombre).</p>
                      )}
                      {modoEjecucion === 'VISUALIZACION' && (
                        <p>Ver coincidencias <strong>sin crear casos</strong>. Útil para análisis exploratorio.</p>
                      )}
                    </div>

                    {/* Campo de búsqueda de cliente */}
                    {modoEjecucion === 'CLIENTE' && (
                      <div className="space-y-2">
                        <Label className="text-sm text-slate-700">Buscar Cliente:</Label>
                        <div className="relative">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                          <Input placeholder="Ingrese CUIT, CVU o nombre..." value={clienteSearch}
                            onChange={(e) => { setClienteSearch(e.target.value); setClienteSeleccionado(null); }}
                            className="pl-9" />
                          {buscandoCliente && (
                            <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-blue-500" />
                          )}
                        </div>
                        {clientesEncontrados.length > 0 && !clienteSeleccionado && (
                          <div className="border rounded-lg bg-white shadow-lg max-h-48 overflow-y-auto">
                            {clientesEncontrados.map((cliente, idx) => (
                              <button key={idx}
                                onClick={() => {
                                  setClienteSeleccionado(cliente);
                                  setClienteFilter(cliente.cuit || cliente.cvu);
                                  setClienteSearch(cliente.nombre);
                                  setClientesEncontrados([]);
                                }}
                                className="w-full px-3 py-2 text-left hover:bg-blue-50 border-b last:border-b-0 text-sm">
                                <p className="font-medium text-slate-800">{cliente.nombre}</p>
                                <p className="text-xs text-slate-500">
                                  CUIT: {cliente.cuit || 'N/A'} | CVU: {cliente.cvu?.substring(0, 20) || 'N/A'}...
                                </p>
                              </button>
                            ))}
                          </div>
                        )}
                        {clienteSeleccionado && (
                          <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded">
                            <div>
                              <p className="text-sm font-medium text-green-800">{clienteSeleccionado.nombre}</p>
                              <p className="text-xs text-green-600">{clienteSeleccionado.cuit || clienteSeleccionado.cvu}</p>
                            </div>
                            <Button size="sm" variant="ghost"
                              onClick={() => { setClienteSeleccionado(null); setClienteFilter(''); setClienteSearch(''); }}
                              className="text-green-700 hover:text-green-900">
                              <X size={16} />
                            </Button>
                          </div>
                        )}
                        {clienteSearch && clientesEncontrados.length === 0 && !buscandoCliente && !clienteSeleccionado && (
                          <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                            <p className="text-amber-700">No se encontró en el padrón.</p>
                            <Button size="sm" variant="outline" onClick={() => setClienteFilter(clienteSearch)}
                              className="mt-2 text-amber-700 border-amber-300">
                              Usar "{clienteSearch}" como filtro
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Alert Generation Options */}
              {executeAllResults.total_coincidencias > 0 && modoEjecucion !== 'VISUALIZACION' && (
                <Card className="p-4 bg-slate-50 border-slate-200">
                  <h4 className="font-medium text-slate-900 mb-3">Opciones de Generación</h4>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <Label className="text-sm text-slate-600 min-w-32">Modo:</Label>
                      <div className="flex gap-2">
                        <Button size="sm" variant={alertGenerationMode === 'individual' ? 'default' : 'outline'}
                          onClick={() => setAlertGenerationMode('individual')}
                          className={alertGenerationMode === 'individual' ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}>
                          Alertas Individuales
                        </Button>
                        <Button size="sm" variant={alertGenerationMode === 'grouped' ? 'default' : 'outline'}
                          onClick={() => setAlertGenerationMode('grouped')}
                          className={alertGenerationMode === 'grouped' ? 'bg-purple-600 hover:bg-purple-700 text-white' : ''}>
                          Casos por Tipo
                        </Button>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 bg-white p-2 rounded border">
                      {alertGenerationMode === 'individual' ? (
                        <p>Se generará una alerta por cada coincidencia detectada ({executeAllResults.total_coincidencias} alertas potenciales).</p>
                      ) : (
                        <p>Se creará <strong>1 caso por cada tipo de monitor</strong> seleccionado. Total: {executeAllResults.detalle_por_monitor.filter(m => m.coincidencias > 0 && selectedMonitorsForAlerts.includes(m.id)).length} caso(s) con {executeAllResults.total_coincidencias} detecciones.</p>
                      )}
                    </div>
                    {executeAllResults.detalle_por_monitor.filter(m => m.coincidencias > 0).length > 1 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm text-slate-600">Monitores a incluir:</Label>
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" className="text-xs"
                              onClick={() => setSelectedMonitorsForAlerts(executeAllResults.detalle_por_monitor.filter(m => m.coincidencias > 0).map(m => m.id))}>
                              Seleccionar todos
                            </Button>
                            <Button size="sm" variant="ghost" className="text-xs"
                              onClick={() => setSelectedMonitorsForAlerts([])}>Limpiar</Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                          {executeAllResults.detalle_por_monitor.filter(m => m.coincidencias > 0).map(monitor => (
                            <button key={monitor.id}
                              onClick={() => {
                                setSelectedMonitorsForAlerts(prev =>
                                  prev.includes(monitor.id) ? prev.filter(id => id !== monitor.id) : [...prev, monitor.id]
                                );
                              }}
                              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                selectedMonitorsForAlerts.length === 0 || selectedMonitorsForAlerts.includes(monitor.id)
                                  ? 'bg-green-100 border-green-300 text-green-800'
                                  : 'bg-slate-100 border-slate-200 text-slate-500'
                              }`}>
                              {monitor.nombre.substring(0, 25)}{monitor.nombre.length > 25 ? '...' : ''} ({monitor.coincidencias})
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          {selectedMonitorsForAlerts.length === 0
                            ? 'Todos los monitores serán incluidos'
                            : `${selectedMonitorsForAlerts.length} monitor(es) seleccionado(s)`}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </div>
          ) : null}
        </div>

        {/* Opción para forzar creación de nuevos casos */}
        {executeAllResults && executeAllResults.total_coincidencias > 0 && alertGenerationMode === 'grouped' && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <input type="checkbox" id="forzar-nuevos" checked={forzarNuevosCasos}
              onChange={(e) => setForzarNuevosCasos(e.target.checked)}
              className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded" />
            <label htmlFor="forzar-nuevos" className="text-sm text-amber-800">
              <span className="font-medium">Forzar creación de nuevos casos</span>
              <span className="text-amber-600 block text-xs">
                Crear casos aunque ya existan para este período (puede generar duplicados)
              </span>
            </label>
          </div>
        )}

        <DialogFooter className="border-t pt-4">
          {executeAllLoading && (
            <div className="flex-1 flex items-center gap-2 text-blue-700 text-sm mr-4">
              <Info size={16} /><span>Por favor espere mientras se analizan los monitores...</span>
            </div>
          )}
          {!executeAllLoading && executeAllResults && executeAllResults.total_coincidencias > 0 && (
            <div className="flex-1 flex items-center gap-2 text-amber-700 text-sm mr-4">
              <Info size={16} /><span>Debe generar las alertas antes de cerrar o los resultados se perderán</span>
            </div>
          )}
          <Button variant="outline" disabled={executeAllLoading}
            onClick={() => {
              if (executeAllResults && executeAllResults.total_coincidencias > 0) {
                const confirmed = window.confirm('¿Está seguro que desea cerrar? Los resultados del análisis se perderán y deberá volver a ejecutar los monitores para generar alertas.');
                if (!confirmed) return;
              }
              setIsExecuteAllDialogOpen(false);
            }}>
            {executeAllLoading ? 'Analizando...' : 'Cerrar'}
          </Button>
          {executeAllResults && executeAllResults.total_coincidencias > 0 && (
            <Button onClick={handleGenerateAlertsFromResults}
              disabled={generateAlertsAfterBacktest || (modoEjecucion === 'CLIENTE' && !clienteFilter && !clienteSeleccionado)}
              className={
                modoEjecucion === 'VISUALIZACION' ? 'bg-purple-600 hover:bg-purple-700 text-white' :
                alertGenerationMode === 'grouped' ? 'bg-green-600 hover:bg-green-700 text-white' :
                'bg-blue-600 hover:bg-blue-700 text-white'
              }>
              {generateAlertsAfterBacktest ? (
                <><Loader2 className="animate-spin mr-2" size={16} />
                  {modoEjecucion === 'VISUALIZACION' ? 'Analizando...' : 'Procesando...'}</>
              ) : (
                <>
                  {modoEjecucion === 'VISUALIZACION' ? (
                    <><Eye size={16} className="mr-2" />Ver Coincidencias</>
                  ) : modoEjecucion === 'CLIENTE' ? (
                    <><User size={16} className="mr-2" />Crear Caso para Cliente</>
                  ) : alertGenerationMode === 'grouped' ? (
                    <><Briefcase size={16} className="mr-2" />Crear/Actualizar Casos</>
                  ) : (
                    <><CheckCircle size={16} className="mr-2" />Generar {executeAllResults.total_coincidencias} Alertas</>
                  )}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Global Config Dialog */}
    <Dialog open={showGlobalConfigDialog} onOpenChange={setShowGlobalConfigDialog}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Settings className="text-slate-600" size={20} />
            Configuración Global de Monitores
          </DialogTitle>
          <DialogDescription>Configure parámetros que afectan a todos los monitores</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Fecha Base Global</Label>
            <p className="text-xs text-slate-500 mb-2">Los monitores solo analizarán datos a partir de esta fecha</p>
            <Input type="date" value={globalConfig.fechaBaseGlobal || ''}
              onChange={(e) => setGlobalConfig({ ...globalConfig, fechaBaseGlobal: e.target.value || null })} />
          </div>
          {globalConfig.fechaBaseGlobal && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                Los monitores analizarán datos desde el <strong>{new Date(globalConfig.fechaBaseGlobal).toLocaleDateString('es-ES')}</strong>
              </p>
            </div>
          )}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <p className="text-xs text-slate-600">
              <strong>Nota:</strong> Si no se define una fecha base, cada monitor usará su configuración individual o los últimos 365 días por defecto.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowGlobalConfigDialog(false)}>Cancelar</Button>
          <Button onClick={saveGlobalConfig} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Save size={16} className="mr-2" />Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
);

export default MonitorsDialogs;
